// IndustryContext — reads /api/tenant/industry once at boot, exposes
// { industry, labels, modules, hasModule() } to every component. Powers
// terminology swaps (e.g. "Work Order" → "Service Call" for HVAC) and
// module gating (HVAC sees refrigerant log, roofers see weather radar).
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const DEFAULT = {
  industry: "pm",
  name: "Property Management",
  labels: {
    work_order: "Work Order", wo: "WO",
    customer: "Tenant", customer_plural: "Tenants",
    site: "Property", job: "Job",
    customer_portal: "Tenant Portal",
  },
  modules: [],
  loading: true,
};

const IndustryCtx = createContext(DEFAULT);

export function IndustryProvider({ children }) {
  const [state, setState] = useState(DEFAULT);

  const load = useCallback(async () => {
    const token = localStorage.getItem("access_token");
    if (!token) { setState({ ...DEFAULT, loading: false, reload: load }); return; }
    try {
      const r = await api.get("/tenant/industry");
      setState({
        industry: r.data.industry,
        industry_locked: !!r.data.industry_locked,
        name: r.data.name,
        labels: { ...DEFAULT.labels, ...r.data.labels },
        modules: r.data.modules || [],
        available: r.data.available || [],
        loading: false,
        reload: load,
      });
    } catch { setState({ ...DEFAULT, loading: false, reload: load }); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onStorage = () => load();
    const onAuth = () => load();
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth-changed", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth-changed", onAuth);
    };
  }, [load]);

  return <IndustryCtx.Provider value={state}>{children}</IndustryCtx.Provider>;
}

export function useIndustry() {
  return useContext(IndustryCtx);
}

// Convenience: label swap with fallback
export function useLabel(key, fallback = "") {
  const { labels } = useIndustry();
  return labels?.[key] || fallback || key;
}

// Convenience: module gating
export function useHasModule(name) {
  const { modules } = useIndustry();
  return (modules || []).includes(name);
}
